import axios from 'axios';
import { reactive, readonly } from 'vue';
import { useGetters } from './getters';

// Shared reactive state across all instances
const currentContent = reactive({});

/**
 * Delete content from localStorage
 */
const deleteContent = () => {
    localStorage.removeItem('page_content');
    Object.keys(currentContent).forEach(key => delete currentContent[key]);
};

/**
 * Set content in localStorage and reactive state
 */
const setContent = (content) => {
    localStorage.setItem('page_content', JSON.stringify(content));
    Object.keys(currentContent).forEach(key => delete currentContent[key]);
    Object.assign(currentContent, content);
};

/**
 * Get content from localStorage
 */
const getContent = () => {
    try {
        return JSON.parse(localStorage.getItem('page_content')) || {};
    } catch {
        return {};
    }
};

/**
 * Composable for managing page content using Composition API
 * @returns {Object} Content management methods and state
 */
export function usePageContent() {
    // Call useGetters inside the composable so it has access to the Vuex store
    const { canChangeLoyaltyStatus, canEnrollLoyalty } = useGetters();

    /**
     * Process HTML content based on login status
     */
    const processHtmlContent = (html, isLoggedIn) => {
        const parser = new DOMParser();
        const description = parser.parseFromString(html, 'text/html');
        
        const loggedInElements = description.querySelectorAll('.logged-in');
        loggedInElements.forEach(element => {
            if (isLoggedIn) {
                element.classList.remove('d-none');
            } else {
                element.classList.add('d-none'); 
            }
        });

        const loggedOutElements = description.querySelectorAll('.logged-out');
        loggedOutElements.forEach(element => {
            if (isLoggedIn) {
                element.classList.add('d-none');
            } else {
                element.classList.remove('d-none'); 
            }
        });

        const signMeUpElements = description.querySelectorAll('.sign-me-up');
        signMeUpElements.forEach(element => {
            if (canEnrollLoyalty.value && canChangeLoyaltyStatus.value) {
                element.classList.remove('d-none');
            } else {
                element.classList.add('d-none'); 
            }
        });

        return description.body.innerHTML;
    };
    /**
     * Fetch page content for a route from API
     */
    const getPageContentForRoute = async (route, isLoggedIn) => {
        deleteContent();
        try {
            let slug = '', isShoppingList = false;
            if (route.params && route.params.slug) {
                slug = route.params.slug;
            } else if (route.path !== '/') {
                const pathSegments = route.path.split('/').filter(segment => segment !== '');
                slug = pathSegments[pathSegments.length - 1] || pathSegments[0] || '';
            }

            if (route.name === 'shoppingList') {
                isShoppingList = true;
            }
            
            if (route.name === 'editProductPlanner') {
                slug = 'edit-product-planner';
            }

            const response = await axios.post(
                process.env.VUE_APP_API_BASE + 'auth/getContentBySlug',
                { 
                    slug: slug,
                    is_shopping_list: isShoppingList,
                },
                {
                    headers: {
                        'Content-type': 'application/json',
                        Authorization: 'Bearer ' + localStorage.getItem('token'),
                    },
                }
            );

            setContent({});
            if (response.data.success && response.data.data.content) {
                const content = response.data.data.content;
                if (content.description) {
                    content.description = processHtmlContent(content.description, isLoggedIn);
                }

                if (content.description_below_dynamic) {
                    content.description_below_dynamic = processHtmlContent(content.description_below_dynamic, isLoggedIn);
                }
                setContent(content || {});
            }
        } catch (error) {
            console.log('Error fetching content:', error);
            setContent({});
        }
    };

    return {
        content: readonly(currentContent),
        getPageContentForRoute,
        deleteContent: () => deleteContent(),
        setContent: (content) => setContent(content),
        getContent,
        processHtmlContent
    };
}